<template>
  <BaseClearButton @click="commentsModal?.$el?.present()" color="light" size="small">
    <i class="icon-message-square text-blue-500 mr-1"></i>
    <span class="text-blue-500 mr-1">{{ comments?.length || 0 }}</span>
    <span>Comment{{ comments?.length === 1 ? '' : 's' }}</span>
  </BaseClearButton>

  <IonModal ref="commentsModal">
    <IonHeader class="ion-no-border px-[10px]">
      <IonToolbar color="white" class="border-b-2 border-gray-300">
        <IonTitle color="medium">{{ title || 'Comments' }}</IonTitle>
        <IonButtons slot="end">
          <IconButton icon="icon-x" @click="commentsModal?.$el?.dismiss()"></IconButton>
        </IonButtons>
      </IonToolbar>
    </IonHeader>
    <IonContent>
      <IonRefresher :mode="refresherMode" v-if="props.onRefresh" slot="fixed" @ion-refresh="props.onRefresh">
        <IonRefresherContent :mode="refresherMode"></IonRefresherContent>
      </IonRefresher>
      <div v-if="!comments?.length" class="text-gray-500 italic p-4">No comments here yet...</div>
      <IonList lines="none" class="p-2">
        <CommentRow v-for="c of comments" :key="c.id" :comment="c" @delete-clicked="removeComment(c.id)"></CommentRow>
      </IonList>
    </IonContent>
    <IonFooter color="white" class="ion-no-border p-[10px] pb-[30px]">
      <IonToolbar color="white" class="border-t-2 border-gray-300">
        <IonTextarea color="medium" v-model="commentInput.value" placeholder="Add comment"></IonTextarea>
        <IonButtons slot="end">
          <IonSpinner v-if="loading" class="mr-1" name="crescent" color="primary"></IonSpinner>
          <IconButton size="large" v-else @click="addComment()" :disabled="!trimmedInput" icon="icon-plus-circle">
          </IconButton>
        </IonButtons>
      </IonToolbar>
    </IonFooter>
  </IonModal>
</template>

<script setup lang="ts">
import { IComment } from '@ncss/models';
import BaseClearButton from '../forms/BaseClearButton.vue';
import { useFormControl } from '@/composables/use-form-control';
import { computed, ref, useTemplateRef } from 'vue';
import {
  IonButtons,
  IonHeader,
  IonTitle,
  IonToolbar,
  IonModal,
  IonContent,
  IonRefresher,
  IonRefresherContent,
  IonList,
  IonFooter,
  IonTextarea,
  IonSpinner,
} from '@ionic/vue';
import IconButton from '../forms/IconButton.vue';
import CommentRow from './CommentRow.vue';
import { useRefresherMode } from '@/composables/use-refresher-mode';

const props = defineProps<{
  title?: string;
  comments?: IComment[];
  onRefresh?: (e?: CustomEvent) => Promise<void>;
  onRemoveComment?: (commentId: string) => Promise<void>;
  onAddComment?: (text: string) => Promise<void>;
}>();

const commentsModal = useTemplateRef('commentsModal');
const commentInput = useFormControl(() => '');
const trimmedInput = computed(() => (commentInput.value as string)?.trim());
const loading = ref(false);
const { mode: refresherMode } = useRefresherMode();

async function addComment() {
  const text = trimmedInput.value;
  if (text && props.onAddComment) {
    loading.value = true;
    await props.onAddComment(text);
    loading.value = false;
    commentInput.value = '';
  }
}

async function removeComment(commentId: string) {
  if (commentId && props.onRemoveComment) {
    loading.value = true;
    await props.onRemoveComment(commentId);
    loading.value = false;
  }
}
</script>

<style scoped>
ion-textarea {
  --color: var(--ion-color-dark);
  --placeholder-color: var(--ion-color-medium);
}
</style>
